import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService , authService, contentService } from '../../services/api';
import { useUser } from '../../context/UserContext';
import { useAuthorContentNavigation } from '../../hooks/useAuthorContentNavigation';

import AdminDashboard from './AdminDashboard';
import AuthorDashboard from './AuthorDashboard';
import InstructorDashboard from './InstructorDashboard';
import ManagerDashboard from './ManagerDashboard';
import LearnerDashboard from './LearnerDashboard';
import SkillsSelectionPopup from '../SkillsSelectionPopup';

const Dashboard = () => {
    const { user, requiresSkills, handleSkillsCompleted, checkSkillsRequirement } = useUser();
    const [dashboardData, setDashboardData] = useState(null);
    const mountRef = React.useRef(true);
    
    const [showSkillsPopup, setShowSkillsPopup] = useState(false);
    const [skillsCheckComplete, setSkillsCheckComplete] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const navigate = useNavigate();

    // A new workspace opens on the Add content screen (K21.2 pt 41): admins and authors land there while the
    // library is empty, once per browser session. An admin goes through the Author role, as the Insights
    // "add content" links do: the first admin of a new workspace has no Author role (both workspace creation
    // paths give account admin, admin and learner), and the screen is for authors.
    const navigateToContent = useAuthorContentNavigation();
    useEffect(() => {
        const roleName = user?.role_name?.toLowerCase();
        if (!(roleName === 'admin' || roleName === 'author')) return;
        let alreadyShown = false;
        try { alreadyShown = sessionStorage.getItem('beetsol-empty-library-opened') === '1'; } catch { /* storage unavailable */ }
        if (alreadyShown) return;
        contentService.getLimits()
            .then((response) => {
                if (Number(response.data?.activeCount) !== 0) return undefined;
                try { sessionStorage.setItem('beetsol-empty-library-opened', '1'); } catch { /* storage unavailable */ }
                if (roleName === 'author') return navigate('/contents/add');
                return navigateToContent();
            })
            .catch(() => {});
        // Removed 10 Oct 2026 (K21.2 pt 41): the check that skipped the redirect for a user without the Author role.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.role_name]);

    useEffect(() => {
        const checkAndShowSkillsPopup = async () => {
            if (user && !skillsCheckComplete) {
                try {
                    const response = await authService.getCurrentUser();
                    // If server indicates skills are required, do not auto-open the popup here.
                    // The learner dashboard provides an inline skills UI; avoid forcing a modal on page load.
                    if (response.data.success && response.data.requiresSkills) {
                     //console.log('User needs to select skills, auto-popup disabled');
                        setShowSkillsPopup(false);
                    } else {
                        setShowSkillsPopup(false);
                    }
                } catch (error) {
                    console.error('Error checking user skills requirement:', error);
                    // If there's an error, check locally
                    const needsSkills = checkSkillsRequirement();
                    setShowSkillsPopup(needsSkills);
                }
                setSkillsCheckComplete(true);
            }
        };

        checkAndShowSkillsPopup();
    }, [user, skillsCheckComplete, checkSkillsRequirement]);


    const fetchData = async () => {
        if (!user || !mountRef.current) return;
        mountRef.current = false;

        try {
            setDashboardLoading(true);
            const response = await dashboardService.getDashboardData();
            setDashboardData(response.data.dashboardData);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
        finally {
            setDashboardLoading(false);
            mountRef.current = false;
        }
    }

    useEffect(() => {
        if (user && skillsCheckComplete && !showSkillsPopup) {
            fetchData();
        }
    }, [user, skillsCheckComplete, showSkillsPopup]);

    const handleSkillsSelected = () => {
        console.log('Skills selected, closing popup');
        setShowSkillsPopup(false);
        handleSkillsCompleted();
        setTimeout(() => {
            fetchData();
        }, 500);
    };
    
    if (!user) {
        return (
        <div className="dashboard-error">
            <p>Unable to load user data. Please try refreshing the page.</p>
        </div>
        );
    }

    const renderDashboard = () => {
        if (!user) return null;
        
        const roleName = user.role_name?.toLowerCase();
        
        if (roleName === 'admin' || roleName === 'account-admin') {
            return <AdminDashboard data={{ user, dashboardData }} />;
        } else if (roleName === 'manager') {
            return <ManagerDashboard data={{ user, dashboardData }} />;
        } else if (roleName === 'author') {
            return <AuthorDashboard data={{ user, dashboardData }} />;
        } else if (roleName === 'learner') {
            return <LearnerDashboard data={{ user, dashboardData }} />;
        } else if (roleName === 'instructor') {
            return <InstructorDashboard data={{ user, dashboardData }} />;
        }else {
            // Default dashboard for other roles
            return <LearnerDashboard data={{ user, dashboardData }} />;
        }
    };

    return (
        <div className="dashboard-container">
        {/* CHANGE: Add Skills Selection Popup */}
        <SkillsSelectionPopup 
            isOpen={showSkillsPopup}
            onSkillsSelected={handleSkillsSelected}
            required={true}
        />
        
        {/* CHANGE: Add overlay to prevent interaction when popup is open */}
        {showSkillsPopup && (
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 999,
                    pointerEvents: 'none' // Allow popup to be interactive
                }}
            />
        )}
        
        {/* Main dashboard content */}
        <div className={`dashboard-content ${showSkillsPopup ? 'dashboard-blurred' : ''}`}
            style={{
                filter: showSkillsPopup ? 'blur(3px)' : 'none',
                pointerEvents: showSkillsPopup ? 'none' : 'auto',
                transition: 'filter 0.3s ease'
            }}
        >
            {renderDashboard()}
        </div>
        </div>
  );
};

export default Dashboard;